import { useState, useMemo } from 'react';
import { useStore } from '../store/useStore';
import { Layout } from '../components/Layout';
import { formatCurrency, getCDMXDate, getCDMXNow, parseCDMXDate } from '../lib/utils';
import { TrendingUp, DollarSign, Package, ArrowDownCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Dashboard() {
    const sales = useStore((state) => state.sales) || [];
    const products = useStore((state) => state.products) || [];
    const users = useStore((state) => state.users) || [];
    const expenses = useStore((state) => state.expenses) || [];

    // Filters
    const [selectedSeller, setSelectedSeller] = useState('all');
    const [selectedPeriod, setSelectedPeriod] = useState('week'); // 'day', 'week', 'month', 'year'
    const [selectedDate, setSelectedDate] = useState(getCDMXDate());

    // Filter Logic
    const { filteredSales, filteredExpenses } = useMemo(() => {
        try {
            const now = getCDMXNow();
            // Safe date parsing
            let specificDayStart, specificDayEnd;
            try {
                specificDayStart = parseCDMXDate(selectedDate);
                specificDayStart.setHours(0, 0, 0, 0);
                specificDayEnd = parseCDMXDate(selectedDate);
                specificDayEnd.setHours(23, 59, 59, 999);
            } catch (e) {
                // Fallback to today if parsing fails
                specificDayStart = new Date(now);
                specificDayStart.setHours(0, 0, 0, 0);
                specificDayEnd = new Date(now);
                specificDayEnd.setHours(23, 59, 59, 999);
            }

            // Calculate Week Start (Monday)
            const dayOfWeek = now.getDay(); // 0 is Sunday
            const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - diffToMonday);
            startOfWeek.setHours(0, 0, 0, 0);

            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            startOfMonth.setHours(0, 0, 0, 0);
            const startOfYear = new Date(now.getFullYear(), 0, 1);
            startOfYear.setHours(0, 0, 0, 0);

            const fs = sales.filter(s => {
                if (!s) return false;
                // Seller Filter
                if (selectedSeller !== 'all' && s.sellerId !== selectedSeller) return false;
                if (s.correctionNote?.startsWith('CANCELADO')) return false;

                // Date Filter
                let date;
                try {
                    date = s.date ? new Date(s.date) : new Date(); // Fallback date
                } catch {
                    return false;
                }

                if (selectedPeriod === 'day') {
                    return date >= specificDayStart && date <= specificDayEnd;
                }
                if (selectedPeriod === 'week') return date >= startOfWeek;
                if (selectedPeriod === 'month') return date >= startOfMonth;
                if (selectedPeriod === 'year') return date >= startOfYear;

                return true;
            });

            // Filter Expenses 
            const fe = expenses.filter(e => {
                if (!e) return false;
                let date;
                try {
                    date = e.date ? new Date(e.date) : new Date();
                } catch {
                    return false;
                }

                if (selectedPeriod === 'day') {
                    return date >= specificDayStart && date <= specificDayEnd;
                }
                if (selectedPeriod === 'week') return date >= startOfWeek;
                if (selectedPeriod === 'month') return date >= startOfMonth;
                if (selectedPeriod === 'year') return date >= startOfYear;
                return true;
            });

            return { filteredSales: fs, filteredExpenses: fe };
        } catch (err) {
            console.error("Dashboard Filter Error", err);
            return { filteredSales: [], filteredExpenses: [] };
        }
    }, [sales, expenses, selectedSeller, selectedPeriod, selectedDate]);

    // Metrics Calculation
    const totalSales = filteredSales.reduce((acc, sale) => acc + (Number(sale.amount) || 0), 0);

    const grossProfit = filteredSales.reduce((acc, sale) => {
        const product = products.find(p => p.sku === sale.sku);
        const cost = product ? (Number(product.cost) || 0) : 0;
        return acc + ((Number(sale.amount) || 0) - (cost * (Number(sale.quantity) || 0)));
    }, 0);

    const totalExpenses = filteredExpenses.reduce((acc, e) => acc + (Number(e.amount) || 0), 0);
    const netProfit = grossProfit - totalExpenses;

    // Wallet Stats
    const walletSales = filteredSales.filter(s => s.paymentMethod === 'wallet' || s.paymentDetails?.wallet).reduce((acc, s) => {
        if (s.paymentMethod === 'wallet') return acc + (Number(s.amount) || 0);
        if (s.paymentDetails?.wallet) return acc + (Number(s.paymentDetails.wallet) || 0);
        return acc;
    }, 0);
    const walletPercentage = totalSales > 0 ? (walletSales / totalSales) * 100 : 0;

    const inventoryValue = products.reduce((acc, p) => acc + ((Number(p.stockCurrent) || 0) * (Number(p.cost) || 0)), 0);

    // Chart Data
    const chartData = useMemo(() => {
        try {
            if (selectedPeriod === 'day') {
                const data = Array.from({ length: 24 }, (_, i) => ({
                    name: `${i}:00`,
                    sales: 0,
                    hour: i
                }));

                filteredSales.forEach(sale => {
                    try {
                        const date = sale.date ? new Date(sale.date) : new Date();
                        const hour = date.getHours();
                        if (data[hour]) {
                            data[hour].sales += (Number(sale.amount) || 0);
                        }
                    } catch { }
                });

                return data;
            }

            // Default Logic for Week (Mon-Sun)
            const data = [];
            const now = new Date();

            if (selectedPeriod === 'week') {
                const dayOfWeek = now.getDay() || 7;
                const monday = new Date(now);
                monday.setDate(now.getDate() - (dayOfWeek - 1));

                for (let i = 0; i < 7; i++) {
                    const d = new Date(monday);
                    d.setDate(monday.getDate() + i);
                    const dateStr = d.toLocaleDateString('es-MX', { weekday: 'short' });

                    const daySales = filteredSales.filter(s => {
                        try {
                            const sd = new Date(s.date);
                            return sd.getDate() === d.getDate() && sd.getMonth() === d.getMonth();
                        } catch { return false; }
                    }).reduce((acc, s) => acc + (Number(s.amount) || 0), 0);

                    data.push({ name: dateStr, sales: daySales });
                }
            } else if (selectedPeriod === 'month') {
                const year = now.getFullYear();
                const month = now.getMonth();
                const numDays = new Date(year, month + 1, 0).getDate();

                for (let i = 1; i <= numDays; i++) {
                    const daySales = filteredSales.filter(s => {
                        try {
                            const sd = new Date(s.date);
                            return sd.getDate() === i && sd.getMonth() === month;
                        } catch { return false; }
                    }).reduce((acc, s) => acc + (Number(s.amount) || 0), 0);

                    // Show label only for specific intervals to avoid clutter could be handled by Recharts, but sending all data is fine
                    data.push({ name: `${i}`, sales: daySales });
                }
            } else if (selectedPeriod === 'year') {
                for (let i = 0; i < 12; i++) {
                    const monthName = new Date(now.getFullYear(), i, 1).toLocaleDateString('es-MX', { month: 'short' });
                    const monthSales = filteredSales.filter(s => {
                        try {
                            const sd = new Date(s.date);
                            return sd.getMonth() === i && sd.getFullYear() === now.getFullYear();
                        } catch { return false; }
                    }).reduce((acc, s) => acc + (Number(s.amount) || 0), 0);
                    data.push({ name: monthName, sales: monthSales });
                }
            }
            return data;
        } catch (e) {
            console.error("Chart Error", e);
            return [];
        }
    }, [filteredSales, selectedPeriod]);

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Dashboard</h1>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">Resumen y métricas principales de desempeño</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {/* Seller Filter */}
                        <select
                            value={selectedSeller}
                            onChange={(e) => setSelectedSeller(e.target.value)}
                            className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="all">Todos los Vendedores</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                        </select>

                        {/* Period Filter */}
                        <div className="flex bg-white rounded-lg border border-slate-300 overflow-hidden">
                            {[
                                { id: 'day', label: 'Día' },
                                { id: 'week', label: 'Semana' },
                                { id: 'month', label: 'Mes' },
                                { id: 'year', label: 'Año' }
                            ].map(p => (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedPeriod(p.id)}
                                    className={`px-3 py-2 text-sm font-medium transition-colors ${selectedPeriod === p.id
                                        ? 'bg-primary-500 text-white'
                                        : 'hover:bg-slate-50 text-slate-600'
                                        }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        {selectedPeriod === 'day' && (
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-500 ml-2"
                            />
                        )}
                    </div>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Ventas */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-emerald-100 text-emerald-600 rounded-lg">
                                <DollarSign className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500 font-medium">Ventas</p>
                                <h3 className="text-xl font-bold text-slate-900">{formatCurrency(totalSales)}</h3>
                            </div>
                        </div>
                    </div>

                    {/* Utilidad Bruta */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                                <TrendingUp className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500 font-medium">Utilidad Bruta</p>
                                <h3 className="text-xl font-bold text-slate-900">{formatCurrency(grossProfit)}</h3>
                            </div>
                        </div>
                    </div>

                    {/* Gastos Operativos */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-red-100 text-red-600 rounded-lg">
                                <ArrowDownCircle className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500 font-medium">Gastos Ops.</p>
                                <h3 className="text-xl font-bold text-red-600">{formatCurrency(totalExpenses)}</h3>
                            </div>
                        </div>
                    </div>

                    {/* Utilidad Neta Real */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-lg ${netProfit >= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-orange-100 text-orange-600'}`}>
                                <DollarSign className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500 font-medium">Utilidad Neta</p>
                                <h3 className={`text-xl font-bold ${netProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatCurrency(netProfit)}</h3>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Wallet Usage Stats */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col justify-center">
                        <p className="text-slate-500 font-bold text-xs uppercase mb-2">Impacto Monedero</p>
                        <div className="flex items-end gap-2">
                            <h3 className="text-2xl font-black text-purple-600">{walletPercentage.toFixed(1)}%</h3>
                            <span className="text-sm text-slate-400 mb-1">de ventas totales</span>
                        </div>
                        <div className="w-full bg-slate-100 h-2 rounded-full mt-2 overflow-hidden">
                            <div className="bg-purple-500 h-full rounded-full" style={{ width: `${Math.min(walletPercentage, 100)}%` }}></div>
                        </div>
                    </div>

                    {/* Inventory Value */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 col-span-3 flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500 font-medium">Valor Total Inventario (Costo)</p>
                            <h3 className="text-2xl font-bold text-slate-900">{formatCurrency(inventoryValue)}</h3>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-full">
                            <Package className="w-8 h-8 text-slate-400" />
                        </div>
                    </div>
                </div>

                {/* Chart */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-96">
                    <h3 className="text-lg font-bold text-slate-800 mb-6 capitalize">Ventas por Periodo ({selectedPeriod})</h3>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} />
                            <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => `$${val}`} />
                            <Tooltip cursor={{ fill: '#f1f5f9' }} formatter={(val: number) => formatCurrency(val)} />
                            <Bar dataKey="sales" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Layout>
    );
}
